import {
  OPINIONS_BY_ID,
  resolveOpinions,
  ZMANIM,
  type Overrides,
  type ZmanInfo,
} from "@kedaat/definitions";
import {
  specialTimes,
  type GeoPoint,
  type HebrewDayInfo,
  type HebrewYearMonth,
  type Opinion,
  type PlainDate,
  type Profile,
  type SpecialTimes,
  type ZmanimEvaluator,
} from "@kedaat/engine";
import type { Locality } from "@kedaat/locations";

/** All a single day needs; the server-rendered pages provide only this. */
export interface DayCalendar {
  hebrewDayInfo(date: PlainDate): HebrewDayInfo;
}

/** The whole calendar module, including the Hebrew month navigation the luach walks. */
export interface CalendarModule extends DayCalendar {
  hebrewMonthOf(date: PlainDate): HebrewYearMonth;
  hebrewMonthTitle(ym: HebrewYearMonth): string;
  hebrewMonthDates(ym: HebrewYearMonth): PlainDate[];
  addHebrewMonths(ym: HebrewYearMonth, count: number): HebrewYearMonth;
}

export interface ZmanRow {
  zman: ZmanInfo;
  opinion: Opinion;
  ms: number | null;
  /** The user chose a different opinion than the profile's. */
  overridden: boolean;
}

export interface DayResult {
  info: HebrewDayInfo;
  rows: ZmanRow[];
  special: SpecialTimes;
}

const SHABBAT_END = OPINIONS_BY_ID.get("tzeit.deg_8_5")!;

/**
 * The observer's point. Elevation is used only where sunset by elevation is appropriate
 * (SPEC 4.6); elsewhere every elevation-based opinion falls back to sea level.
 */
export function pointFor(locality: Locality): GeoPoint {
  return {
    latitude: locality.latitude,
    longitude: locality.longitude,
    elevation: locality.elevationShkiaEligible ? (locality.elevation ?? 0) : 0,
  };
}

export function computeDay(
  evaluator: ZmanimEvaluator,
  calendar: DayCalendar,
  date: PlainDate,
  profile: Profile,
  overrides: Overrides,
  candleLightingMinutes: number,
): DayResult {
  const info = calendar.hebrewDayInfo(date);
  const opinions = resolveOpinions(profile, overrides);
  const rows = ZMANIM.filter((z) => z.group !== "pesach" || info.isErevPesach).map((zman) => ({
    zman,
    opinion: opinions[zman.id],
    ms: evaluator.evaluate(opinions[zman.id].calc, date),
    overridden: opinions[zman.id].id !== profile.opinions[zman.id],
  }));
  const special = specialTimes(evaluator, info, {
    candleLightingMinutes,
    sunset: opinions.sunset.calc,
    alos: opinions.alos.calc,
    tzeit: opinions.tzeit.calc,
    shabbatEnd: SHABBAT_END.calc,
    rabbeinuTam: opinions.tzeit_rt.calc,
  });
  return { info, rows, special };
}

const gregorianFormat = new Intl.DateTimeFormat("he-IL", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

export function formatGregorian(date: PlainDate): string {
  return gregorianFormat.format(Date.UTC(date.year, date.month - 1, date.day));
}

/** "בעוד 42 דק׳" / "בעוד 2 שע׳ ו-5 דק׳" */
export function formatCountdown(ms: number): string {
  const totalMinutes = Math.max(1, Math.ceil(ms / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `בעוד ${minutes} דק׳`;
  if (minutes === 0) return `בעוד ${hours} שע׳`;
  return `בעוד ${hours} שע׳ ו-${minutes} דק׳`;
}
